import { parseRangeFromTs } from '@mi-front/core';
import { Chip, MrtTable, ResultsCount, ViewLoader } from '@mi-front/ui';
import { FormattedMessage } from 'react-intl';
import { useDebounce } from 'use-debounce';
import { LTime } from '../../../../components/date/LTime';
import { useFocus } from '../../../logger/screens/stream/stores/stream-screen.store';
import { useAuditLiveMentionsMatches } from '../../hooks/useAuditLiveMentionsMatches';

import { LiveMentionConfidenceControls } from '@app/modules/logger/screens/stream/components/audit/focus-markers/live-mentions/components/LiveMentionConfidenceControls';
import { LiveMentionPlayBtn } from '@app/modules/logger/screens/stream/components/audit/focus-markers/live-mentions/components/LiveMentionPlayBtn';
import { LiveMentionShowButton } from '@app/modules/logger/screens/stream/components/audit/focus-markers/live-mentions/components/LiveMentionShowButton';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { IconButton } from '@mui/material';
import { useIntl } from 'react-intl';
import { Station } from '@app/core/types/stations';

interface Props {
  readonly station: Station;
}

export function WaveLiveMentionsStatusView(p: Props) {
  const { $t } = useIntl();
  const focus = useFocus();
  const [debouncedFocus] = useDebounce(focus, 300);
  const mentionsQuery = useAuditLiveMentionsMatches(p.station, parseRangeFromTs(debouncedFocus));

  const mentions = mentionsQuery.data;

  if (mentionsQuery.isLoading) {
    return <ViewLoader />;
  }

  return mentions && mentions.length > 0 ? (
    <MrtTable
      initialState={{ columnVisibility: { 'mrt-row-expand': false } }}
      columns={[
        {
          id: 'time',
          header: $t({
            id: 'audit.props.time',
            defaultMessage: 'Hora',
          }),
          Cell: ({ row: { original: mention } }) => (
            <LTime value={mention.datetime} second={'numeric'} />
          ),
          size: 120,
        },
        {
          header: $t({
            id: 'audit.props.type',
            defaultMessage: 'Tipo',
          }),
          Cell: ({ row: { original: mention } }) => (
            <Chip type="primary" size="sm">
              {$t({
                id: 'audit.liveMention.badge',
                defaultMessage: 'Live Mention',
              })}
            </Chip>
          ),
          size: 120,
        },
        {
          header: $t({
            id: 'common.text.name',
            defaultMessage: 'Nombre',
          }),
          accessorKey: 'name',
          enableColumnFilter: false,
          size: 120,
        },
        {
          header: $t({
            id: 'common.text.description',
            defaultMessage: 'Descripción',
          }),
          accessorKey: 'description',
          enableColumnFilter: false,
          size: 200,
        },
        {
          header: $t({
            id: 'common.status',
            defaultMessage: 'Estado',
          }),
          Cell: ({ row: { original: mention } }) => (
            <Chip
              size="sm"
              type={
                mention.confidence === 'ACCEPTED'
                  ? 'success'
                  : mention.confidence === 'REJECTED'
                    ? 'danger'
                    : 'light'
              }
            >
              <FormattedMessage
                id={`audit.liveMentions.confidence.${mention.confidence.toLowerCase()}`}
                defaultMessage={mention.confidence}
              />
            </Chip>
          ),
          size: 100,
        },
        {
          header: $t({
            id: 'common.text.actions',
            defaultMessage: 'Acciones',
          }),
          Cell: ({ row: { original: mention, getIsExpanded, id }, table }) => (
            <div className="d-flex justify-content-around">
              <LiveMentionPlayBtn liveMention={mention} />
              <LiveMentionShowButton liveMention={mention} />
              <LiveMentionConfidenceControls
                stationId={p.station.id}
                id={mention.id}
                className="m-0"
              />
              {mention.type === 'zeroshot' ? null : (
                <IconButton
                  size="small"
                  style={{ color: 'var(--grey)' }}
                  onClick={() => {
                    table.setExpanded({ [id]: !getIsExpanded() });
                  }}
                >
                  {getIsExpanded() ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                </IconButton>
              )}
            </div>
          ),
        },
      ]}
      data={mentions}
      renderDetailPanel={({ row }) => {
        return row.original.type === 'zeroshot' ? null : (
          <div>{(row.original.customData as any)?.content}</div>
        );
      }}
      displayColumnDefOptions={{
        'mrt-row-expand': {
          enableHiding: false,
          visibleInShowHideMenu: false, //now row numbers are hidable too
        },
      }}
      enableExpandAll={false}
    />
  ) : (
    <ResultsCount count={0}>
      <FormattedMessage
        id="audit.liveMentionsScreen.noResults"
        defaultMessage="No se encontraron matches"
        values={{ count: 0 }}
      />
    </ResultsCount>
  );
}
